/**
 * Badge — la pastille en capitales des captures MoneyMaker : un mot, un fond
 * doux de la couleur de son TON, un point qui luit si c'est un statut.
 * Retapé au lot 2 de la refonte du catalogue (28-10-2026) ; les deux
 * `PastilleStatut` des familles tableau et ventes se dessinent avec lui.
 *
 *   <Badge ton="vert" point>Actif</Badge>
 *   <Badge ton="ambre">En attente</Badge>
 *   <Badge ton="marque">Toi</Badge>
 *   <Infobulle texte="Payé le 2 novembre"><Badge ton="vert">Payé</Badge></Infobulle>
 *
 * Les tons portent un SENS, le même sous toutes les marques (Kayla, 27-10 :
 * « le vert, c'est la couleur quand quelque chose est validé ») : `vert` =
 * validé, actif, payé ; `ambre` = attention, en attente ; `rouge` = erreur,
 * suspendu, remboursé ; `gris` = neutre, au repos. `marque` est l'exception
 * voulue : ce qui est À TOI (« Toi » dans un classement) — un accent
 * d'interaction, pas un statut. Jamais de plein à la marque avec du texte
 * blanc dessus (l'ancien `active`) : le texte reste dans la teinte.
 *
 * Il transmet sa `ref` et tout attribut HTML (les gestes, `aria-*`, `data-*`) à
 * son `<span>` : une `Infobulle` l'enveloppe directement (vague 2, 02-11).
 *
 * Les anciens noms (`variant`) restent acceptés pour les 29 fichiers du
 * cockpit qui les écrivent : active → vert, inactive / neutral → gris,
 * warning → ambre, role → marque. Sans rien : `active`, comme avant.
 */
import type { HTMLAttributes, ReactNode, Ref } from "react";

import { cn } from "./utils";
import "./lecture.css";

export type TonBadge = "gris" | "vert" | "ambre" | "rouge" | "marque";
/** Les noms d'avant le lot 2 — chacun vaut un ton. */
export type BadgeVariant = "active" | "inactive" | "neutral" | "warning" | "role";
export type BadgeSize = "sm" | "md";

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  ton?: TonBadge;
  /** L'ancien nom ; `ton` l'emporte s'il est donné. */
  variant?: BadgeVariant;
  /** Le point qui luit, à gauche : c'est un statut. */
  point?: boolean;
  size?: BadgeSize;
  icon?: ReactNode;
  title?: string;
  className?: string;
  children: ReactNode;
  /** Le `<span>` — pour une `Infobulle` qui l'enveloppe, ou pour le mesurer. */
  ref?: Ref<HTMLSpanElement>;
}

export const TON_DE_LA_VARIANTE: Record<BadgeVariant, TonBadge> = {
  active: "vert",
  inactive: "gris",
  neutral: "gris",
  warning: "ambre",
  role: "marque",
};

export function Badge({ ton, variant, point = false, size = "sm", icon, title, className, children, ref, ...rest }: BadgeProps) {
  const t = ton ?? TON_DE_LA_VARIANTE[variant ?? "active"];
  return (
    <span ref={ref} {...rest} className={cn("bd lc-pilule", className)} data-ton={t} data-taille={size} title={title}>
      {point && <span className="bd-point lc-rond" aria-hidden />}
      {icon !== undefined && <span className="bd-icone">{icon}</span>}
      {children}
    </span>
  );
}
